import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';

import {
  getLabelFallback,
  getContactDetail,
  getItemUnitTotalPrice,
  getItemUnitDiscountedTotalPrice,
  convertNumber,
  labels,
} from '@utils/utils';
import ButtonLink from '@components/ButtonLink';
import CustomSelect, { CustomOption } from '@components/CustomSelect';
import {
  editSavedItemsContactQuantityAsync,
  selectEditSavedItemsContactQuantityStatus,
  selectConfig,
  selectFormattedOrderItems,
  setFormattedOrderItems,
} from '../../store/wishlist/wishlistSlice';
import {
  EditSavedItemsContactQuantityPayload,
  WishlistItem,
} from '@store/wishlist/wishlistInterfaces';
import LoaderElement from '@components/skeletons/LoaderElement';

interface Props {
  handleDeleteItem: (orderItemId?: string) => void;
  isInStock: boolean;
  item: WishlistItem;
  isContactLoading: boolean;
}

const options: CustomOption[] = Array(13)
  .fill('')
  .map((_: string, i: number) => ({ value: i.toString(), label: i.toString() }));

export default function WishlistContactItemContent({
  handleDeleteItem,
  isInStock,
  item,
  isContactLoading,
}: Props) {
  const dispatch: any = useDispatch();

  const formattedItems = useSelector(selectFormattedOrderItems);
  const config = useSelector(selectConfig);
  const editContactQuantityStatus = useSelector(selectEditSavedItemsContactQuantityStatus);

  const formattedItem = formattedItems[item.idList];

  const handleEditContact = () => {
    const pageUrl = item.catentries[0].attributes.find(attr => attr.name === 'pageUrl')
      .value as string;

    if (pageUrl) window.location.href = `${config.hostname}${pageUrl}?edit=${item.idList}`;
  };

  const showPrice = (): boolean => {
    return (
      formattedItem?.frame.contactLenses && formattedItem.frame.totalAdjustment !== 0 && isInStock
    );
  };

  const leftQuantity: number = formattedItem
    ? getContactDetail(formattedItem, 'left', 'quantity') === '--'
      ? 0
      : getContactDetail(formattedItem, 'left', 'quantity')
    : 0;

  const rightQuantity: number = formattedItem
    ? getContactDetail(formattedItem, 'right', 'quantity') === '--'
      ? 0
      : getContactDetail(formattedItem, 'right', 'quantity')
    : 0;

  const handleChangeQuantity = (opt: CustomOption, eye: 'left' | 'right') => {
    if (opt.value === '0' && (eye === 'left' ? rightQuantity === 0 : leftQuantity === 0)) {
      handleDeleteItem();
      return;
    }
    const itemCopy = cloneDeep(formattedItem);
    const rcon = item.catentries.find(entry => entry.catEntryType === 'RCON');
    const lcon = item.catentries.find(entry => entry.catEntryType === 'LCON');
    const data: EditSavedItemsContactQuantityPayload = {
      savedItemId: item.idList.toString(),
      rightContactCatalogEntryId: rcon?.catEntryId,
      rightContactQuantity: eye === 'right' ? +opt.value : rcon?.quantity,
      leftContactCatalogEntryId: lcon?.catEntryId,
      leftContactQuantity: eye === 'left' ? +opt.value : lcon?.quantity,
    };

    itemCopy[eye + 'Contact'].quantity = +opt.value;

    dispatch(editSavedItemsContactQuantityAsync(data));
    dispatch(setFormattedOrderItems({ ...formattedItems, [item.idList]: itemCopy }));
  };

  return (
    <>
      <div className="wishlist-item-contact-quantity">
        <div className="wishlist-item-contact">
          {config.brand === 'TO' && (
            <div className="wishlist-item-contact-row">
              <b>{getLabelFallback('EYE_CONTACT_LABEL')}</b>
              <b>{getLabelFallback('BC_CONTACT_LABEL')}</b>
              <b>{getLabelFallback('DIA_CONTACT_LABEL')}</b>
              <b>{getLabelFallback('POWER_CONTACT_LABEL')}</b>
              <b>{getLabelFallback('CYL_CONTACT_LABEL')}</b>
              <b>{getLabelFallback('AXIS_CONTACT_LABEL')}</b>
              <b>{getLabelFallback('DOMINANCE_CONTACT_LABEL')}</b>
              <b>
                <ButtonLink onClick={handleEditContact}>
                  {getLabelFallback('EDIT_ITEM_LABEL')}
                </ButtonLink>
              </b>
            </div>
          )}
          <div className="wishlist-item-contact-row">
            <div className="wishlist-item-contact-eye">
              {getLabelFallback(
                ((config.brand === 'TO' ? '' : 'LC_') + 'RIGHT_CONTACT_LABEL') as labels
              )}
            </div>
            {config.brand === 'LC' && (
              <>
                <div>
                  {rightQuantity > 0
                    ? getContactDetail(formattedItem, 'right', 'cl_base_curve')
                    : '--'}
                </div>
                <div>
                  {rightQuantity > 0
                    ? getContactDetail(formattedItem, 'right', 'cl_diameter')
                    : '--'}
                </div>
                <div>
                  {rightQuantity > 0
                    ? getContactDetail(formattedItem, 'right', 'cl_sphere_power')
                    : '--'}
                </div>
                <div>
                  {rightQuantity > 0
                    ? getContactDetail(formattedItem, 'right', 'cl_cylinder')
                    : '--'}
                </div>
                <div>
                  {rightQuantity > 0 ? getContactDetail(formattedItem, 'right', 'cl_axis') : '--'}
                </div>
              </>
            )}
            {config.brand === 'TO' && (
              <>
                <div>
                  {rightQuantity > 0
                    ? getContactDetail(formattedItem, 'right', 'cl_base_curve')
                    : '--'}
                </div>
                <div>
                  {rightQuantity > 0
                    ? getContactDetail(formattedItem, 'right', 'cl_diameter')
                    : '--'}
                </div>
                <div>
                  {rightQuantity > 0 ? getContactDetail(formattedItem, 'right', 'cl_sphere_power') : '--'}
                </div>
                <div>
                  {rightQuantity > 0
                    ? getContactDetail(formattedItem, 'right', 'cl_cylinder')
                    : '--'}
                </div>
                <div>
                  {rightQuantity > 0 ? getContactDetail(formattedItem, 'right', 'cl_axis') : '--'}
                </div>
                <div>
                  {rightQuantity > 0 ? getContactDetail(formattedItem, 'right', 'cl_dominance')==='True'? 'D':'N' : '--'}
                </div>
              </>
            )}
            <div>
              {isContactLoading && <LoaderElement height={32} width={80} radius={4} />}
              {rightQuantity > 0 && (
                <CustomSelect
                  value={{ value: rightQuantity, label: rightQuantity.toString() }}
                  onChange={opt => {
                    handleChangeQuantity(opt, 'right');
                  }}
                  options={options}
                  isLoading={editContactQuantityStatus === 'LOADING'}
                  haveDelete
                />
              )}
            </div>
            {/* right price */}
            {showPrice() && config.brand === 'LC' && (
              <div className="wishlist-item-contact-price-container">
                <div className="wishlist-item-price-container">
                  <label className="wishlist-item-price-discounted">
                    {getLabelFallback('CURRENCY_LABEL')}
                    {convertNumber(+getItemUnitTotalPrice(formattedItem) * rightQuantity)}
                  </label>
                </div>
              </div>
            )}
          </div>
          <div className="wishlist-item-contact-row">
            <div className="wishlist-item-contact-eye">
              {getLabelFallback(
                ((config.brand === 'TO' ? '' : 'LC_') + 'LEFT_CONTACT_LABEL') as labels
              )}
            </div>
            {config.brand === 'LC' && (
              <>
                <div>
                  {leftQuantity > 0
                    ? getContactDetail(formattedItem, 'left', 'cl_base_curve')
                    : '--'}
                </div>
                <div>
                  {leftQuantity > 0 ? getContactDetail(formattedItem, 'left', 'cl_diameter') : '--'}
                </div>
                <div>
                  {leftQuantity > 0
                    ? getContactDetail(formattedItem, 'left', 'cl_sphere_power')
                    : '--'}
                </div>
                <div>
                  {leftQuantity > 0 ? getContactDetail(formattedItem, 'left', 'cl_cylinder') : '--'}
                </div>
                <div>
                  {leftQuantity > 0 ? getContactDetail(formattedItem, 'left', 'cl_axis') : '--'}
                </div>
              </>
            )}
            {config.brand === 'TO' && (
              <>
                <div>
                  {leftQuantity > 0
                    ? getContactDetail(formattedItem, 'left', 'cl_base_curve')
                    : '--'}
                </div>
                <div>
                  {leftQuantity > 0
                    ? getContactDetail(formattedItem, 'left', 'cl_diameter')
                    : '--'}
                </div>
                <div>
                  {leftQuantity > 0 ? getContactDetail(formattedItem, 'left', 'cl_sphere_power') : '--'}
                </div>
                <div>
                  {leftQuantity > 0
                    ? getContactDetail(formattedItem, 'left', 'cl_cylinder')
                    : '--'}
                </div>
                <div>
                  {leftQuantity > 0 ? getContactDetail(formattedItem, 'left', 'cl_axis') : '--'}
                </div>
                <div>
                  {leftQuantity > 0 ? getContactDetail(formattedItem, 'left', 'cl_dominance')==='True'? 'D':'N' : '--'}
                </div>
              </>
            )}
            <div>
              {isContactLoading && <LoaderElement height={32} width={80} radius={4} />}
              {leftQuantity > 0 && (
                <CustomSelect
                  value={{ value: leftQuantity, label: leftQuantity.toString() }}
                  onChange={opt => {
                    handleChangeQuantity(opt, 'left');
                  }}
                  options={options}
                  isLoading={editContactQuantityStatus === 'LOADING'}
                  haveDelete
                />
              )}
            </div>
            {/* Left price */}
            {showPrice() && config.brand === 'LC' && (
              <div className="wishlist-item-contact-price-container">
                <div className="wishlist-item-price-container">
                  <label className="wishlist-item-price-discounted">
                    {getLabelFallback('CURRENCY_LABEL')}
                    {convertNumber(+getItemUnitTotalPrice(formattedItem) * leftQuantity)}
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Unit price */}
        {isContactLoading && (
          <div className="wishlist-item-contact-box-price">
            <div className="wishlist-item-contact-price-container">
              <LoaderElement height={16} width={72} />
              <LoaderElement height={16} width={44} />
            </div>
          </div>
        )}
        {showPrice() && (
          <div className="wishlist-item-contact-box-price">
            <div className="wishlist-item-contact-price-container">
              <p>{getLabelFallback('PRICES_PER_BOX_LABEL')}</p>
              <div className="wishlist-item-price-container">
                {config.brand === 'TO' &&
                  getItemUnitTotalPrice(formattedItem) !==
                    getItemUnitDiscountedTotalPrice(formattedItem) && (
                    <label className="wishlist-item-price">
                      {getLabelFallback('CURRENCY_LABEL')}
                      {getItemUnitTotalPrice(formattedItem)}
                    </label>
                  )}
                <label className="wishlist-item-price-discounted">
                  {getLabelFallback('CURRENCY_LABEL')}
                  {config.brand === 'TO'
                    ? getItemUnitDiscountedTotalPrice(formattedItem)
                    : getItemUnitTotalPrice(formattedItem)}
                </label>
              </div>
            </div>
            {config.brand === 'LC' && (
              <div className="wishlist-item-contact-subtotal-container">
                <p>
                  {`${leftQuantity + rightQuantity} ${getLabelFallback(
                    'LC_PRICES_BOX_SUBTOTAL_LABEL'
                  )}`}
                </p>
                {/* subtotal price */}
                <div className="wishlist-item-contact-price-container subtotal">
                  <div className="wishlist-item-price-container">
                    <label className="wishlist-item-price-discounted">
                      {getLabelFallback('CURRENCY_LABEL')}
                      {convertNumber(
                        +getItemUnitTotalPrice(formattedItem) * leftQuantity + rightQuantity
                      )}
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {showPrice() && (
        <div className="cart-product-tile-right-bottom-cl-container-supply desktop">
          <span>
            {getLabelFallback(
              ((config.brand === 'TO' ? '' : 'LC_') + 'ANNUAL_SUPPLY_LABEL') as labels
            )
              .replace('##CURRENCY##', getLabelFallback('CURRENCY_LABEL'))
              .replace('##VALUE##', formattedItem.frame.totalAdjustment.toString())}
          </span>
          <span className="total-adjustment">
            {config.brand === 'LC' &&
              `-${getLabelFallback('CURRENCY_LABEL')}${convertNumber(
                formattedItem.frame.totalAdjustment
              )}`}
          </span>
        </div>
      )}
    </>
  );
}
